import _ from 'lodash'

/**
 * Creates a new type of {@link TmrResource TMR Resource}.
 *
 * Use as return value of a factory. When defining a new resource type,
 * you should also add the corresponding endpoints in {@link tmrDefaultApiEndpoints}.
 *
 * @example
 * function myResourceFactory(TmrResource) { 'ngInject';
 *   return TmrResource('MyResource');
 * }
 * export const factory = ['MyResource', myResourceFactory];
 *
 * @example <caption>User resource with additional action</caption>
 * TmrResource('User', {
 *   getLogged: {
 *     method: 'GET',
 *     url: TmrApi.endpoint('loggedUser')
 *   }
 * });
 *
 * @example <caption>Antenna resource with referenced property</caption>
 * TmrResource('Antenna', {}, [{ property: 'rfidReader', resource: RfidReader }]);
 *
 * @example <caption>Antenna resource with default value</caption>
 * TmrResource('Antenna', {}, [], () => ({ someProperty: 42 }));
 *
 * @alias TmrResource
 * @variation 2
 * @type {ngFactory}
 * @param {string} resourceType The resource type. Must correspond to the API Endpoint key.
 * @param {object} additionalActions Resource additional actions.
 * @param {array} references A list of properties that will instantiate as referenced TmrResources.
 * @param {function=} defaultValues Function that returns the values that will be added to the resource after each action.
 * @param {boolean=} [printable=false] If true, the resource can be printed and the `printablePagedQuery` and `$print` actions are available.
 * @returns {TmrResource}
 */
function TmrResourceFactory(
  $http,
  $resource,
  $translate,
  TmrAdminResourceRoutes,
  TmrFile,
  TmrTransformResourceAction,
  TmrApi,
  TmrSearchFormFilters,
  tmrDefaultExportFormat,
  tmrDefaultPagingParams,
  tmrApiAuthToken,
  tmrLimitZeroWorkaround
) {
  'ngInject'
  return TmrResourceConstructor

  function TmrResourceConstructor(
    resourceType,
    additionalActions,
    references,
    defaultValues,
    printable = false
  ) {
    references = references ?? []
    let url = TmrApi.endpoint(resourceType)
    let queryUrl = url.replace(/\/:id$/, '')
    let actions = _.defaultsDeep(additionalActions ?? {}, {
      /**
       * @method TmrResource#get
       * @returns {TmrResource}
       */
      get: { method: 'GET' },

      /**
       * @method TmrResource#save
       */
      save: { method: 'POST' },

      /**
       * @method TmrResource#bulkCreate
       */
      bulkCreate: {
        url: `${queryUrl}/bulkCreate`,
        method: 'POST',
        isArray: true,
      },

      /**
       * @method TmrResource#bulkUpdate
       */
      bulkUpdate: {
        url: `${queryUrl}/bulkUpdate`,
        method: 'POST',
        isArray: true,
      },

      /**
       * Supports filtering and sorting on any field
       *
       * WARNING: The query returns all results. Use with caution or it might bring
       * down the server!
       *
       * @method TmrResource#query
       * @returns {array<TmrResource>}
       */
      query: {
        url: queryUrl,
        method: 'GET',
        isArray: true,
        params: { offset: 0, limit: tmrLimitZeroWorkaround },
      },

      /**
       * Like query, but supports pagination with defaults from {@link tmrDefaultPagingParams}
       *
       * @method TmrResource#pagedQuery
       * @returns {array<TmrResource>}
       */
      pagedQuery: {
        url: queryUrl,
        method: 'GET',
        isArray: true,
        params: Object.assign({}, tmrDefaultPagingParams),
      },

      /**
       * Fast search query with no filters, useful for autocomplete
       *
       * @method TmrResource#search
       * @returns {array<TmrResource>}
       */
      search: { url: `${queryUrl}/search`, method: 'GET', isArray: true },

      /**
       * Advanced search query that accepts filters as a POST. Defaults to a limit of 50.
       *
       * @method TmrResource#advancedSearch
       * @returns {array<TmrResource>}
       */
      advancedSearch: {
        url: `${queryUrl}/advancedSearch`,
        method: 'POST',
        isArray: true,
      },

      /**
       * @method TmrResource#delete
       */
      delete: { method: 'DELETE' },

      /**
       * Same as delete
       * @method TmrResource#remove
       */
      remove: { method: 'DELETE' },
    })

    if (printable) {
      /**
       * Same as pagedQuery, but with print counters added to each result.
       *
       * To use this action in a resourceAdmin list route, add the param to the route definition:
       * `params: {
       *   searchAction: 'printablePagedQuery'
       * }`
       *
       * @method TmrResource#printablePagedQuery
       */
      actions.printablePagedQuery = actions.printablePagedQuery ?? {
        url: TmrApi.endpoint(`${resourceType}.printable`),
        method: 'GET',
        isArray: true,
        params: Object.assign({}, tmrDefaultPagingParams),
      }
    }

    for (let actionName in actions) {
      const action = actions[actionName]
      // Add authorization token key so auth can fill it.
      action.headers = action.headers ?? {}
      if (action.headers[tmrApiAuthToken] === undefined)
        action.headers[tmrApiAuthToken] = null

      // Add transformResponse to instantiate referenced resources.
      references.forEach(({ property, resource }) =>
        TmrTransformResourceAction.appendResponse(
          action,
          (data, headersGetter, status) => {
            // Transform only if the response status is OK
            if (status >= 200 && status < 300) {
              if (action.isArray)
                data.forEach(res =>
                  TmrResource.createReferences(res, property, resource)
                )
              else TmrResource.createReferences(data, property, resource)
            }
            return data
          }
        )
      )

      // Add default values.
      if (defaultValues !== undefined)
        TmrTransformResourceAction.defaultValues(action, defaultValues)
    }

    /**
     * Standard TMR Resource.
     *
     * Custom resource types (such as User, Place, ecc) inherit the methods of this base type,
     * and can extend its behavior in many ways, as documented by {@link TmrResource(2)}
     *
     * Do *not* instantiate a resource using the standard operator `new MyResource(defaults)`;
     * instead, always call the `{@link TmrResource#create create}` method: `MyResource.create(defaults)`.
     *
     * @alias TmrResource
     * @class
     */
    let TmrResource = $resource(url, { id: '@id' }, actions)

    Object.assign(TmrResource, {
      count,
      export: exportQuery,
      getType,
      loadTypeLabel,
      getLabelProperty,
      create,
      createReferences,
      printable,
    })

    Object.assign(TmrResource.prototype, {
      $count: count,
      $getType: getType,
      $loadTypeLabel,
      $getLabelProperty: getLabelProperty,
      $getLabel,
      $labelIncludes,
      $canDelete,
      $getRoute,
      $printable: printable,
    })

    if (printable) {
      /**
       * Prints the resource.
       *
       * @method TmrResource#print
       */
      TmrResource.prototype.$print = function (printer, quantity) {
        return TmrApi.post(
          `${resourceType}.print`,
          { id: this.id },
          { printer, quantity }
        )
      }

      /**
       * Same as count, but counts printable resources (which sometimes may differ from normal resources).
       *
       * To use this action in a resourceAdmin list route, add the param to the route definition:
       * `params: {
       *   countAction: 'printableCount'
       * }`
       *
       * @method TmrResource#printableCount
       */
      TmrResource.printableCount = function (originalParams) {
        // Don't change passed params.
        let params = Object.assign({}, originalParams)
        return TmrApi.get(
          `${resourceType}.printableCount`,
          {},
          { params }
        ).then(({ data }) => data.value)
      }
    }

    return TmrResource

    /**
     * Count action with filtering on any field.
     *
     * This is a simple `$http` query that returns a promise, because we don't
     * want to carry around an object as required by `$resource`.
     *
     * @method TmrResource#count
     * @returns {Promise<number>}
     */
    function count(originalParams) {
      // Don't change passed params.
      let params = Object.assign({}, originalParams)
      // If ends with :id, it's a simple resource route like /items; if not, it's
      // a nested resource route like /inventories/:id/items and the "id" parameter
      // must be replaced manually.
      let countUrl
      if (url.endsWith(':id')) {
        countUrl = url.replace(':id', 'count')
      } else {
        if (params.id === undefined)
          throw new Error('Count query requested with missing parameter "id"')
        countUrl = url.replace(':id', params.id) + '/count'
        delete params.id
      }
      return $http
        .get(countUrl, { headers: { [tmrApiAuthToken]: null }, params })
        .then(({ data }) => data.value)
    }

    /**
     * Export action, like query but responds by serving a downloaded file.
     *
     * @method TmrResource#export
     *
     * @param {string} format The exported file format (csv, xlsx, etc...)
     * @param {boolean} [download=true] Pass the file to the browser for download
     * @returns {Promise<File>}
     */
    function exportQuery(
      format = tmrDefaultExportFormat,
      params = TmrSearchFormFilters.params,
      download = true
    ) {
      // add orderBy to filterParams if exists
      if (!!TmrSearchFormFilters.$stateParams.orderBy) {
        params.orderBy = TmrSearchFormFilters.$stateParams.orderBy
      }

      let request = TmrApi.get(
        resourceType,
        { id: `export/${format}` },
        { params }
      )
      return TmrFile.download(
        request,
        `export.${format}`,
        null,
        download
      ).catch(err => {
        throw new Error(err.data.message)
      })
    }

    /**
     * Returns the resource type.
     *
     * @method TmrResource#getType
     * @returns {string}
     */
    function getType() {
      return resourceType
    }

    /**
     * Returns a promise for the translated label of the resource type.
     *
     * @method TmrResource#loadTypeLabel
     * @returns {Promise<string>}
     */
    function loadTypeLabel() {
      return $translate('RESOURCETYPES.' + getType().toUpperCase())
    }

    /**
     * Returns a promise for the translated label of the resource type.
     * Also saves the translated label in resource.$typeLabel
     *
     * @method TmrResource#$loadTypeLabel
     * @returns {Promise<string>}
     */
    function $loadTypeLabel() {
      let promise = loadTypeLabel()
      promise.then(label => (this.$typeLabel = label))
      return promise
    }

    /**
     * Returns the property that holds the label value.
     *
     * @method TmrResource#getLabelProperty
     * @returns {string}
     */
    function getLabelProperty() {
      return 'description'
    }

    /**
     * Creates a new TMR Resource with default values.
     *
     * @method TmrResource#create
     * @returns {TmrResource}
     */
    function create(defaults) {
      return TmrResource.createReferences(
        new TmrResource(
          Object.assign({}, (defaultValues || _.noop)(), defaults)
        )
      )
    }

    /**
     * Creates TMR Resources in referenced properties with default values.
     *
     * @method TmrResource#createReferences
     * @returns {TmrResource}
     */
    function createReferences(resource) {
      if (resource === undefined || resource === null) return // Probably a request error occurred: skip.

      references.forEach(reference => {
        if (resource[reference.property] === null) return // Optional references may be null: skip.

        if (typeof resource[reference.property] === 'object') {
          // @todo Use _.set to allow for stuff like parcel.parcelItems[].item
          if (Array.isArray(resource[reference.property])) {
            resource[reference.property] = resource[reference.property].map(
              value => reference.resource.create(value)
            )
          } else {
            resource[reference.property] = reference.resource.create(
              resource[reference.property]
            )
          }
        }
      })
      return resource
    }

    /**
     * Builds a label for this resource.
     *
     * @method TmrResource#$getLabel
     * @prop {boolean=} [truncate=true]
     * @returns {string}
     */
    function $getLabel(truncate = true) {
      let label = this[this.$getLabelProperty()] || this.code
      // Last resort: show the second part of the guid. Similar to git "short guid".
      return (
        label ||
        (this.id ? (truncate ? this.id.split('-', 2)[1] + '...' : this.id) : '')
      )
    }

    /**
     * Returns true if the resource label includes text.
     *
     * @method TmrResource#$labelIncludes
     * @prop {string} text
     * @returns {boolean}
     */
    function $labelIncludes(text) {
      return this.$getLabel()
        .toLocaleLowerCase()
        .includes(text.toLocaleLowerCase())
    }

    /**
     * Verifies if this resource can be deleted.
     *
     * By default, it's always true. Specific resource types can add server calls
     * and other logic to determine if the resource can be deleted.
     *
     * @returns {Promise<string|boolean>} If can delete, value is true; if can't delete,
     * value is a translation string for the reason.
     */
    function $canDelete() {
      return Promise.resolve(true)
    }

    /**
     * Returns the standard route/state name for this resource type.
     *
     * @param {'list'|'view'|'add'|'edit'|'delete'} op The requested CRUD route
     */
    function $getRoute(op) {
      return TmrAdminResourceRoutes[op + 'RouteName'](this.$getType())
    }
  }
}

export const factory = ['TmrResource', TmrResourceFactory]
